/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import { useEffect, useState } from "react";
import { ListItemProps } from "./types";
import { sleep } from "@Components/LeadFormModal/utils";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import styles from "./listItemStyles.module.scss";
import CashBackBannerIcon from "@Icons/CashbackBannerIcon";
import ArrowIcon from "@Icons/ArrowIcon";
import OfferChevronRight from "@Icons/OfferChevronRight";
import PropertyDetailsModal from "../PropertyDetailsModal";
import CheckIcon from "@Icons/CheckIcon";
import CloseIcon from "@Icons/CloseIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import convertHtmlToPlainText from "@Utils/clientUtils/htmlToText/convertHtmlToPlainText";

const renderListItemIcon = (
  listIcon: ListItemProps["listIcon"],
  isHouseRules: boolean,
  listIconContainerClassName: string,
  iconContainerClassName: string,
  fallbackIcon: JSX.Element,
) => {
  if (!listIcon) {
    return <div className={iconContainerClassName}>{fallbackIcon}</div>;
  }

  if (isHouseRules) {
    return listIcon;
  }

  return <div className={listIconContainerClassName}>{listIcon}</div>;
};

const ListItem = ({
  defaultOpen = false,
  title,
  gtagEventAction,
  content,
  onClose,
  shortText,
  gtagExtraParams = {},
  withIcon = false,
  showCheckIconFromProps = false,
  showCrossIconFromProps = false,
  showInfoIconFromProps = false,
  actionButtonText = "Got it",
  prefixContent,
  suffixContent,
  showArrow = true,
  id = "",
  isDesktop = false,
  fireLoginEvent = false,
  listIcon = null,
  titleClassName = "",
  disableOnClick = false,
  isHouseRules = false,
  isCancellation = false,
  minimizeContentCharLength = 0,
  titleForLogic = "",
}: ListItemProps) => {
  const { isWhitelabel, whitelabelColors } = useWhitelabel();
  const [openModal, setOpenModal] = useState(defaultOpen);
  // @ts-ignore
  const formattedTitle = title.split("_").join(" ");
  const { t } = useTranslationHook();
  const viewButtonLabelText = t("View");
  let sanitizedShortText = convertHtmlToPlainText(replaceAmberDomainInLinks(shortText || ""));
  const sliceSanitizedShortText = sanitizedShortText.slice(0, minimizeContentCharLength);
  sanitizedShortText =
    isHouseRules || sanitizedShortText.length < 280
      ? sliceSanitizedShortText
      : `${sliceSanitizedShortText}...`;

  if (isHouseRules) {
    sanitizedShortText = sanitizedShortText.replace(/^"|"$/g, "");
  }
  useEffect(() => {
    if (defaultOpen) {
      (async () => {
        await sleep(15);
        gtagEventEmitter({
          action: `viewed_${gtagEventAction}`,
          category: PAGE.INVENTORY,
          label: title,
          ...gtagExtraParams,
        });
      })();
    }
  }, []);
  actionButtonText = t("bookingpage.components.modalactionbutton.gotit");
  const handleCloseListItem = () => {
    setOpenModal(false);
    if (typeof onClose === "function") {
      onClose();
    }
  };
  const showCheckIcon = showCheckIconFromProps && !showInfoIconFromProps;
  const showCrossIcon = showCrossIconFromProps && !showInfoIconFromProps;
  // const showInfoIcon =
  //   showInfoIconFromProps || (!showCheckIconFromProps && !showCrossIconFromProps && !withIcon);

  const isCancellationPolicyStatic = (titleText: any) => titleText.split(" ")[0] === "cancellation";

  // Helper functions to get appropriate CSS classes
  const getContainerClassName = () => {
    if (isCancellation) return styles.cancellationListItemContainer;
    if (isHouseRules) return styles.houseRulesListItemContainer;
    return styles.listItemContainer;
  };

  const getListItemClassName = () => {
    if (isCancellation) return styles.cancellationListItem;
    if (isHouseRules) return styles.houseRulesListItem;
    return styles.listItem;
  };

  const getContentClassName = () => {
    if (isCancellation) return styles.cancellationItemContent;
    if (isHouseRules) return styles.houseRulesItemContent;
    return styles.listItemContent;
  };

  const handleOpenListItem = (e: any) => {
    e.preventDefault();

    if (disableOnClick) return;

    setOpenModal(true);
    gtagEventEmitter({
      action: `viewed_${gtagEventAction}`,
      category: PAGE.INVENTORY,
      label: title,
      ...gtagExtraParams,
    });
    if (fireLoginEvent) {
      gtagEventEmitter({
        action: `user_login_on_offer_click`,
        category: PAGE.INVENTORY,
        label: title,
        offer_id: id,
      });
    }
  };

  const handleOpenListItemForCancellation = (e: any) => {
    handleOpenListItem(e);
  };

  useEffect(() => {
    if (typeof title === "string" && isCancellationPolicyStatic(titleForLogic)) {
      window.addEventListener("open_cancellation_modal", handleOpenListItemForCancellation);
    }

    return () => {
      if (typeof title === "string" && isCancellationPolicyStatic(titleForLogic)) {
        window.removeEventListener("open_cancellation_modal", handleOpenListItemForCancellation);
      }
    };
  }, []);
  const SelectedIcon = () => {
    if (showCheckIcon) {
      return <CheckIcon height="20px" width="20px" fill="#0E9F6E" />;
    }
    if (showCrossIcon) {
      return (
        <CloseIcon
          strokeWidth="2"
          size={20}
          stroke="#fff"
          style={{ background: "#9CA3AF", borderRadius: "50%", padding: 4, color: "#fff" }}
        />
      );
    }
    return <CashBackBannerIcon {...(isWhitelabel && { fill: whitelabelColors?.brand_color })} />;
  };

  const listItemIcon = renderListItemIcon(
    listIcon,
    isHouseRules,
    styles.listIconContainer,
    styles.iconContainer,
    <SelectedIcon />,
  );

  const titleClassNames = formatClassNames(
    styles.listHeading,
    isDesktop && styles.desktop,
    titleClassName,
    isCancellation && styles.cancellationTitle,
    isHouseRules && !isCancellation && styles.houseRulesTitle,
  );

  const renderTitle = () => (
    <div data-testid="property-policy-name" className={titleClassNames}>
      {formattedTitle}
    </div>
  );

  const renderHouseRulesDetails = () => {
    if (!sanitizedShortText && !(shortText && shortText.length > 100)) {
      return null;
    }

    return (
      <div
        data-testid="property-policy-details"
        className={formatClassNames(
          styles.houseRulesDetailsContainer,
          withIcon && styles.houseRulesDetailsIndented,
        )}
      >
        {sanitizedShortText ? (
          <div className={styles.houseRulesDescription}>{sanitizedShortText}</div>
        ) : null}
        {shortText && shortText.length > 100 && (
          <div className={styles.houseRulesViewMoreLink}>
            {t("View more")}
            <OfferChevronRight width={12} height={12} color="#111928" />
          </div>
        )}
      </div>
    );
  };

  const useHouseRulesIconLayout = isHouseRules && !isCancellation && withIcon;

  return (
    <li className={getContainerClassName()}>
      <div
        data-testid="Policies"
        className={formatClassNames(
          getListItemClassName(),
          useHouseRulesIconLayout && styles.houseRulesListItemWithIcon,
        )}
        role="button"
        onKeyDown={disableOnClick ? undefined : handleOpenListItem}
        onClick={disableOnClick ? undefined : handleOpenListItem}
      >
        {useHouseRulesIconLayout ? (
          <>
            <div className={styles.houseRulesTitleRow}>
              {listItemIcon}
              {renderTitle()}
            </div>
            {renderHouseRulesDetails()}
          </>
        ) : (
          <>
            {withIcon && listItemIcon}

            <div className={formatClassNames(getContentClassName())}>
              {renderTitle()}
              {/* @ts-ignore */}
              {isCancellation && (
                <div
                  data-testid="property-policy-details"
                  className={styles.cancellationDetailsContainer}
                >
                  <div className={styles.cancellationDescription}>{sanitizedShortText}</div>
                  {shortText && shortText.length > 100 && (
                    <div className={styles.cancellationViewMoreLink}>
                      {t("View more")}
                      <OfferChevronRight width={12} height={12} color="#111928" />
                    </div>
                  )}
                </div>
              )}
              {!isCancellation && isHouseRules && renderHouseRulesDetails()}
              {!isCancellation && !isHouseRules && (
                <div
                  dangerouslySetInnerHTML={{ __html: replaceAmberDomainInLinks(shortText) }}
                  className={styles.listSubHeading}
                  data-testid="property-policy-details"
                />
              )}
            </div>
            {isHouseRules ? null : <div className={styles.growDiv} />}

            {isHouseRules ? null : (
              <div data-testid="property-policy-view-button" className={styles.listHeadingAnchor}>
                {showArrow ? <ArrowIcon rotate={270} stroke="black" /> : viewButtonLabelText}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal dialog */}
      {openModal && (
        <PropertyDetailsModal
          header={formattedTitle}
          actionButtonText={actionButtonText}
          hidePropertyModal={handleCloseListItem}
          isDesktop={isDesktop}
        >
          {prefixContent && <>{prefixContent}</>}
          {typeof content === "string" ? (
            <div dangerouslySetInnerHTML={{ __html: replaceAmberDomainInLinks(content) }} />
          ) : (
            content
          )}
          {suffixContent && <>{suffixContent}</>}
        </PropertyDetailsModal>
      )}
    </li>
  );
};

export default wrapperHOC(ListItem, {
  componentName: "ListItem-PropertyPage",
  showForChina: true,
});
